import type {
  AnimalSource,
  AnimalStatus,
  EventType,
  MilkSession,
  ProtocolTarget,
  ProtocolType,
  ReminderStatus,
  ReminderType,
  Sex,
  WithheldReason,
} from '@dairy/shared';

/** Minimal structural shapes the domain needs. Device records and API rows both satisfy them. */

export interface DomainAnimal {
  id: string;
  tag: string;
  name?: string | null;
  sex: Sex;
  breed?: string;
  birthDate: string;
  status: AnimalStatus;
  exitDate?: string | null;
  openingParity: number;
  source?: AnimalSource;
  purchaseDate?: string | null;
  damId?: string | null;
  clientCreatedAt?: string;
  deletedAt?: string | null;
}

export interface DomainEvent {
  id: string;
  animalId: string;
  type: EventType;
  date: string;
  data: Record<string, unknown>;
  isOpening?: boolean;
  costSantim?: number | null;
  note?: string | null;
  clientCreatedAt?: string;
  createdAt?: string;
  createdBy?: string;
  deletedAt?: string | null;
}

export interface DomainMilkRecord {
  id?: string;
  animalId: string;
  date: string;
  session: MilkSession;
  ml: number;
  withheld: boolean;
  withheldReason?: WithheldReason | null;
  lateEntry?: boolean;
  deletedAt?: string | null;
}

export interface DomainUsage {
  date: string;
  calfMl: number;
  homeMl: number;
  spoiledMl: number;
  deletedAt?: string | null;
}

export interface DomainProtocol {
  id: string;
  type: ProtocolType;
  name: string;
  product?: string | null;
  intervalDays: number;
  target: ProtocolTarget;
  firstDueAgeDays?: number | null;
  active: boolean;
  clientCreatedAt?: string;
  deletedAt?: string | null;
}

export interface DomainReminder {
  id: string;
  animalId: string;
  type: ReminderType;
  dueDate: string;
  windowEnd?: string | null;
  status: ReminderStatus;
  sourceEventId?: string | null;
  protocolId?: string | null;
  deletedAt?: string | null;
}

export interface DomainSale {
  id: string;
  type: 'MILK' | 'ANIMAL' | 'OTHER';
  date: string;
  buyerId?: string | null;
  milkMl?: number | null;
  unitPriceSantim?: number | null;
  amountSantim: number;
  animalId?: string | null;
  createdAt?: string;
  clientCreatedAt?: string;
  deletedAt?: string | null;
}

export interface DomainPayment {
  id: string;
  buyerId: string;
  date: string;
  amountSantim: number;
  saleId?: string | null;
  createdAt?: string;
  clientCreatedAt?: string;
  deletedAt?: string | null;
}

export interface DomainCategory {
  id: string;
  name: string;
  nameAm: string;
  parentId?: string | null;
  costType: 'OPERATING' | 'CAPITAL';
  isFeed: boolean;
  systemKey?: string | null;
  active?: boolean;
  deletedAt?: string | null;
}

export interface DomainExpense {
  id: string;
  date: string;
  categoryId: string;
  amountSantim: number;
  coversMonths: number;
  animalId?: string | null;
  sourceEventId?: string | null;
  deletedAt?: string | null;
}

export function live<T extends { deletedAt?: string | null }>(rows: readonly T[]): T[] {
  return rows.filter((r) => !r.deletedAt);
}
